import { FC, useState } from 'react';
import { Button, DynamicComponent, HeaderText, LuvImage } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  TypeInputTextFields,
  TypeKeyValueListFields,
} from '@/core/generated-types';
import { generateTranslationsObject } from '@/utils/translations';
import { Asset, Entry } from 'contentful';
import { pushToDataLayer } from '@/utils/dataLayer';

export interface EmailCaptureProps {
  translations: Entry<Record<string, any>>;
  image: Asset;
  emailInput: Entry<TypeInputTextFields>;
}

const EmailCapture: FC<EmailCaptureProps> = ({
  translations,
  image,
  emailInput,
}) => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const t = generateTranslationsObject(
    translations.fields as TypeKeyValueListFields,
  );
  const {
    fields: { requiredError },
  } = emailInput;

  const validateEmailInput = (emailInput: any) => {
    if (!emailInput.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return false;
    }

    return true;
  };

  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    if (!email || !validateEmailInput(email)) {
      setErrorMessage(requiredError ?? '');
      return;
    }

    try {
      const signUpEvent = {
        event: 'newsletterSignup',
        newsLetterEmail: email,
        emailPreferences:
          "What's new with my baby’s development (stage-based development information), What's new from Lovevery (promotions, sales and product launches)",
        source: 'Main Site',
        optin_email_promotions: true,
        optin_email_developmental: true,
      };
      pushToDataLayer(signUpEvent);

      setErrorMessage('');
      setSuccessMessage(t.success);
      setEmail('');
    } catch (error) {
      setErrorMessage(t.error);
    }
  };

  return (
    <div className="bg-off-white">
      <div className="max-w-[1220px] mx-auto w-full py-10 md:py-10">
        <div className="block md:flex justify-center items-center gap-4">
          <div className="w-full md:w-1/2 p-10 md:p-20">
            <LuvImage Image={image} altText="" className="w-full h-full" />
          </div>
          <div className="w-full md:w-1/2 p-6 md:pr-10 ">
            <div className="max-w-[400px]">
              <HeaderText size={HeaderSize.H2} text={t.title} classes="mb-4" />
              <p className="text-base lg:text-lg font-normal mb-4">
                {t.paragraph}
              </p>
              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-6">
                  <DynamicComponent
                    component={emailInput}
                    value={email}
                    error={errorMessage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                  />
                </div>
                <Button
                  type="submit"
                  ctaText={t.cta}
                  classes="w-full md:w-auto btn-responsive"
                />
              </form>
              {successMessage && (
                <div className="my-4 text-sm text-success">
                  {successMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCapture;
